import { useMemo } from "react";
import { RestClient } from "./restClient";
import { useEmbedApiAuth, makeBearerFetch } from "./embedApiContext";
import { SkillsApi } from "./skillsApi";
import { DepartmentsApi } from "./departmentsApi";
import { DocumentsApi } from "./documentsApi";
import { ConversationKnowledgeApi } from "./conversationKnowledgeApi";
import { PosthogReportsApi } from "./posthogReportsApi";
import { ProposalsApi } from "./proposalsApi";
import { ConnectorsApi } from "./connectorsApi";
import { MarketplaceApi } from "./marketplaceApi";
import { SkillsMarketplaceApi } from "./skillsMarketplaceApi";
import { CompaniesApi } from "./companiesApi";
import { PublisherApi } from "./publisherApi";
import { CustomMcpApi } from "./customMcpApi";
import { ApiKeysApi } from "./apiKeysApi";
import { BudgetSettingsApi } from "./budgetSettingsApi";
import { EmailSettingsApi } from "./emailSettingsApi";
import { ProfileApi } from "./profileApi";
import { LearningSettingsApi } from "./learningSettingsApi";
import { KbSettingsApi } from "./kbSettingsApi";
import { LlmSettingsApi } from "./llmSettingsApi";
import { CompanyPoliciesApi } from "./companyPoliciesApi";
import { ShortTermMemoryApi } from "./shortTermMemoryApi";
import { FactsApi } from "./factsApi";
import { KnowledgeGrantsApi } from "./knowledgeGrantsApi";
import { RolesApi } from "./rolesApi";
import { AuthApi } from "./authApi";
import { BillingApi } from "./billingApi";
import { OnboardingApi } from "./onboardingApi";
import { OrgSyncApi } from "./orgSyncApi";

interface AuthedApi {
  projects_enabled: boolean;
  subagents_enabled: boolean;
  skills: SkillsApi;
  departments: DepartmentsApi;
  documents: DocumentsApi;
  conversationKnowledge: ConversationKnowledgeApi;
  proposals: ProposalsApi;
  connectors: ConnectorsApi;
  marketplace: MarketplaceApi;
  skillsMarketplace: SkillsMarketplaceApi;
  companies: CompaniesApi;
  publisher: PublisherApi;
  customMcp: CustomMcpApi;
  apiKeys: ApiKeysApi;
  budgetSettings: BudgetSettingsApi;
  emailSettings: EmailSettingsApi;
  profile: ProfileApi;
  learningSettings: LearningSettingsApi;
  kbSettings: KbSettingsApi;
  llmSettings: LlmSettingsApi;
  companyPolicies: CompanyPoliciesApi;
  shortTermMemory: ShortTermMemoryApi;
  facts: FactsApi;
  knowledgeGrants: KnowledgeGrantsApi;
  roles: RolesApi;
  auth: AuthApi;
  billing: BillingApi;
  onboarding: OnboardingApi;
  orgSync: OrgSyncApi;
  posthogReports: PosthogReportsApi;
}

function flagFromEnv(value: string | undefined): boolean {
  return ["1", "true", "yes"].includes((value ?? "").toLowerCase());
}

/**
 * Constructs the cookie-authed REST clients for the workspace pages.
 *
 * Identity rides the HttpOnly `egpt_session` cookie (BFF flow) — there is no
 * token to read or gate on. These pages are wrapped in <RequireAuth>, so by
 * the time this hook runs the user is authenticated; the clients just need the
 * api base url. RestClient routes through `apiFetch` (credentials + silent
 * refresh + login redirect on hard 401).
 */
export function useAuthedApi(): AuthedApi {
  // In the rich embed there is no session cookie — the workspace pages must
  // authenticate with the embed's Bearer userJWT against the embed's API base.
  // The context is null in the normal SPA, so this is a no-op there (cookie).
  const embed = useEmbedApiAuth();
  const apiUrl = embed?.apiUrl ?? import.meta.env.VITE_API_URL ?? "";
  const embedFetch = useMemo(
    () => (embed ? makeBearerFetch(embed.getToken) : undefined),
    [embed],
  );
  const projects_enabled = flagFromEnv(import.meta.env.VITE_PROJECTS_ENABLED);
  const subagents_enabled = flagFromEnv(import.meta.env.VITE_SUBAGENTS_ENABLED);

  // The RETURNED object must be referentially stable, not just the inner
  // clients: callers use the whole hook result as a useCallback/useEffect
  // dependency (e.g. PublisherPage's loadSelf). Spreading into a fresh object
  // on every render made those deps change every render → effect → setState →
  // render: an infinite fetch loop (observed ~16k requests on one page view).
  return useMemo(() => {
    const rest = new RestClient({ apiUrl, fetch: embedFetch });
    return {
      projects_enabled,
      subagents_enabled,
      skills: new SkillsApi(rest),
      departments: new DepartmentsApi(rest),
      documents: new DocumentsApi(rest),
      conversationKnowledge: new ConversationKnowledgeApi(rest),
      posthogReports: new PosthogReportsApi(rest),
      proposals: new ProposalsApi(rest),
      connectors: new ConnectorsApi(rest),
      marketplace: new MarketplaceApi(rest),
      skillsMarketplace: new SkillsMarketplaceApi(rest),
      companies: new CompaniesApi(rest),
      publisher: new PublisherApi(rest),
      customMcp: new CustomMcpApi(rest),
      apiKeys: new ApiKeysApi(rest),
      budgetSettings: new BudgetSettingsApi(rest),
      emailSettings: new EmailSettingsApi(rest),
      profile: new ProfileApi(rest, apiUrl),
      learningSettings: new LearningSettingsApi(rest),
      kbSettings: new KbSettingsApi(rest),
      llmSettings: new LlmSettingsApi(rest),
      companyPolicies: new CompanyPoliciesApi(rest),
      shortTermMemory: new ShortTermMemoryApi(rest),
      facts: new FactsApi(rest),
      knowledgeGrants: new KnowledgeGrantsApi(rest),
      roles: new RolesApi(rest),
      auth: new AuthApi(rest),
      billing: new BillingApi(rest),
      onboarding: new OnboardingApi(rest),
      orgSync: new OrgSyncApi(rest),
    };
  }, [apiUrl, embedFetch, projects_enabled, subagents_enabled]);
}
